/**
 * Shared Navigation Items Configuration
 *
 * Provides consistent navigation items across all pages
 * with conditional Admin menu based on user role.
 *
 * @fileoverview Navigation items helper for sidebar
 */

import { MdHome, MdTimeline, MdAdminPanelSettings, MdLogout, MdAttachMoney, MdPeople, MdCategory, MdEdit, MdDescription, MdPieChart, MdAutoAwesome } from 'react-icons/md';
import type { NavigationItem } from './Sidebar';

/**
 * Get navigation items based on user role
 *
 * @param isAdmin - Whether the current user has global ADMIN role
 * @param isTenantAdmin - Whether the current user is a tenant admin (TENANT_ADMIN or owner)
 * @returns Array of navigation items for sidebar
 *
 * @example
 * const navItems = getNavItems(user?.globalRole === 'ADMIN', selectedTenant?.tenantRole === 'TENANT_ADMIN');
 */
export const getNavItems = (isAdmin: boolean, isTenantAdmin: boolean = false): NavigationItem[] => [
  {
    id: 'home',
    label: 'Home',
    icon: MdHome as NavigationItem['icon'],
    path: '/home',
  },
  {
    id: 'status',
    label: 'Status',
    icon: MdTimeline as NavigationItem['icon'],
    path: '/status',
  },
  {
    id: 'transactions',
    label: 'Transactions',
    icon: MdAttachMoney as NavigationItem['icon'],
    path: '/transactions',
  },
  {
    id: 'transactions-summary',
    label: 'Transactions Summary',
    icon: MdPieChart as NavigationItem['icon'],
    path: '/transactions-summary',
  },
  {
    id: 'statements',
    label: 'Statements',
    icon: MdDescription as NavigationItem['icon'],
    path: '/statements',
  },
  {
    id: 'categories',
    label: 'Categories',
    icon: MdCategory as NavigationItem['icon'],
    path: '/categories',
  },
  ...(isAdmin
    ? [
        {
          id: 'snapshots',
          label: 'Scenario Editor',
          icon: MdEdit as NavigationItem['icon'],
          path: '/snapshots',
        },
        {
          id: 'admin',
          label: 'Admin',
          icon: MdAdminPanelSettings as NavigationItem['icon'],
          path: '/admin',
        },
      ]
    : []),
  {
    id: 'signout',
    label: 'Sign Out',
    icon: MdLogout as NavigationItem['icon'],
    path: '/signout',
  },
];

export default getNavItems;
